import React, { useEffect, useRef, useState, useCallback } from 'react';
import { fabric } from 'fabric';
import { useParams, useNavigate } from 'react-router-dom';
import bannerTemplates from '../../data/bannerTemplates';
import aiBackgroundRemover from '../../utils/aiBackgroundRemoval';
import {
  Box,
  Paper,
  Button,
  Stack,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Divider,
  Chip,
  Card,
  CardContent,
  Tabs,
  Tab,
  Grid,
  Switch,
  FormControlLabel,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
  Alert,
  Snackbar,
  styled,
  Collapse
} from '@mui/material';
import {
  TextFields as TextIcon,
  Image as ImageIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Circle as CircleIcon,
  Stop as RectangleIcon,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  Layers as LayersIcon,
  Visibility,
  VisibilityOff,
  ContentCopy,
  ZoomIn,
  ZoomOut,
  ZoomOutMap,
  Palette,
  GridOn,
  AutoFixHigh,
  Settings,
  Add as AddIcon,
  KeyboardArrowDown,
  ExpandLess,
  ExpandMore,
  Category as ShapesIcon,
  Wallpaper as BackgroundIcon,
  PhotoLibrary as PhotosIcon,
  Dashboard as ElementsIcon,
  Star as StarIcon,
  Remove as LineIcon,
  ArrowForward as ArrowIcon,
  ChangeHistory as PolygonIcon,
  PanoramaFishEye as EllipseIcon,
  PieChart as WedgeIcon,
  Timeline as ArcIcon,
  RadioButtonUnchecked as RingIcon,
  Upload as UploadIcon,
  FormatSize as FontSizeIcon,
  Title as HeadingIcon,
  FormatAlignLeft as ParagraphIcon,
  Close as CloseIcon,
  Help as HelpIcon,
  Keyboard as KeyboardIcon,
  EmojiEmotions as EmojiIcon,
  Category as CategoryIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { HexColorPicker } from 'react-colorful';
import InstationLogo from '../layout/Instation-complete.svg';
import PexelsSearch from './panels/PexelsSearch';

// LinkedIn banner dimensions
const CANVAS_WIDTH = 1584;
const CANVAS_HEIGHT = 396;

// Styled components for modern look
const SidebarContainer = styled(Paper)(({ theme }) => ({
  width: 80,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 0,
  borderRight: 'none',
  overflow: 'hidden',
  backgroundColor: '#ffffff',
  boxShadow: 'none',
  borderRight: '1px solid rgba(0, 0, 0, 0.08)'
}));

const SidebarItem = styled(Box)(({ theme, active }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1.8, 0),
  cursor: 'pointer',
  borderLeft: active ? '3px solid #4890fe' : '3px solid transparent',
  backgroundColor: active ? 'rgba(72, 144, 254, 0.08)' : 'transparent',
  '&:hover': {
    backgroundColor: active ? 'rgba(72, 144, 254, 0.12)' : 'rgba(0, 0, 0, 0.03)',
  },
}));

const SidebarIcon = styled(Box)(({ theme, active }) => ({
  color: active ? '#4890fe' : '#64748b',
  marginBottom: theme.spacing(0.5),
}));

const SidebarLabel = styled(Typography)(({ theme, active }) => ({
  fontSize: '0.7rem',
  fontWeight: active ? 600 : 500,
  color: active ? '#4890fe' : '#64748b',
  textAlign: 'center',
}));

const ModernButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1, 2),
  justifyContent: 'flex-start',
  textTransform: 'none',
  fontWeight: 500,
  color: theme.palette.text.primary,
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
  '& .MuiButton-startIcon': {
    marginRight: theme.spacing(1.5),
  },
}));

const ElementButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  padding: theme.spacing(1),
  minWidth: 0,
  width: '100%',
  height: 80,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(0.5),
  textTransform: 'none',
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: 'rgba(72, 144, 254, 0.04)',
    borderColor: '#4890fe',
  },
}));

// Professional templates with Poppins font
const templates = [
  {
    id: 1,
    name: 'Professional Blue',
    preview: '#1E40AF',
    description: 'Clean and professional design',
    elements: [
      { type: 'background', color: '#1E40AF' },
      { type: 'text', text: 'Your Professional Title', x: 100, y: 100, fontSize: 48, color: '#FFFFFF', fontWeight: 'bold', fontFamily: 'Poppins' },
      { type: 'text', text: 'Expertise • Innovation • Results', x: 100, y: 200, fontSize: 24, color: '#93C5FD', fontFamily: 'Poppins' }
    ]
  },
  {
    id: 2,
    name: 'Creative Gradient',
    preview: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
    description: 'Modern gradient design',
    elements: [
      { type: 'background', color: '#667eea' },
      { type: 'text', text: 'Creative Professional', x: 150, y: 120, fontSize: 52, color: '#FFFFFF', fontWeight: 'bold', fontFamily: 'Poppins' },
      { type: 'text', text: 'Design • Strategy • Innovation', x: 150, y: 220, fontSize: 28, color: '#E5E7EB', fontFamily: 'Poppins' }
    ]
  },
  {
    id: 3,
    name: 'Minimalist Black',
    preview: '#000000',
    description: 'Elegant minimalist style',
    elements: [
      { type: 'background', color: '#000000' },
      { type: 'text', text: 'MINIMALIST', x: 100, y: 150, fontSize: 64, color: '#FFFFFF', fontWeight: 'bold', fontFamily: 'Poppins' },
      { type: 'text', text: 'Professional Excellence', x: 100, y: 250, fontSize: 24, color: '#9CA3AF', fontFamily: 'Poppins' }
    ]
  },
  {
    id: 4,
    name: 'Tech Innovation',
    preview: '#0F172A',
    description: 'Modern tech-focused design',
    elements: [
      { type: 'background', color: '#0F172A' },
      { type: 'text', text: 'TECH INNOVATOR', x: 120, y: 120, fontSize: 56, color: '#00D9FF', fontWeight: 'bold', fontFamily: 'Poppins' },
      { type: 'text', text: 'Building the Future of Technology', x: 120, y: 220, fontSize: 26, color: '#94A3B8', fontFamily: 'Poppins' }
    ]
  }
];

// Shapes data
const SHAPES = [
  { id: 'rectangle', name: 'Rectangle', icon: <RectangleIcon /> },
  { id: 'circle', name: 'Circle', icon: <CircleIcon /> },
  { id: 'line', name: 'Line', icon: <LineIcon /> },
  { id: 'arrow', name: 'Arrow', icon: <ArrowIcon /> },
  { id: 'star', name: 'Star', icon: <StarIcon /> },
  { id: 'polygon', name: 'Polygon', icon: <PolygonIcon /> },
  { id: 'ellipse', name: 'Ellipse', icon: <EllipseIcon /> },
  { id: 'wedge', name: 'Wedge', icon: <WedgeIcon /> },
  { id: 'arc', name: 'Arc', icon: <ArcIcon /> },
  { id: 'ring', name: 'Ring', icon: <RingIcon /> }
];

// Emoji categories
const EMOJI_CATEGORIES = [
  {
    name: 'Smileys & People',
    emojis: ['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '☺️', '😚', '😙', '🥲', '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥', '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '🥵', '🥶', '🥴', '😵', '🤯', '🤠', '🥳', '🥸', '😎', '🤓', '🧐', '😕', '😟', '🙁', '☹️', '😮', '😯', '😲', '😳', '🥺', '😦', '😧', '😨', '😰', '😥', '😢', '😭', '😱', '😖', '😣', '😞', '😓', '😩', '😫', '🥱', '😤', '😡', '😠', '🤬', '😈', '👿', '💀', '☠️']
  },
  {
    name: 'Objects',
    emojis: ['⌚', '📱', '📲', '💻', '⌨️', '🖥️', '🖨️', '🖱️', '🖲️', '🕹️', '🗜️', '💽', '💾', '💿', '📀', '📼', '📷', '📸', '📹', '🎥', '📽️', '🎞️', '📞', '☎️', '📟', '📠', '📺', '📻', '🎙️', '🎚️', '🎛️', '🧭', '⏱️', '⏲️', '⏰', '🕰️', '⌛', '⏳', '📡', '🔋', '🔌', '💡', '🔦', '🕯️', '🪔', '🧯', '🛢️', '💸', '💵', '💴', '💶', '💷', '🪙', '💰', '💳', '💎', '⚖️', '🪜', '🧰', '🔧', '🔨', '⚒️', '🛠️', '⛏️', '🪓', '🪚', '🔩', '⚙️', '🪤', '🧱', '⛓️', '🧲', '🔫', '💣', '🧨', '🪓', '🔪', '🗡️', '⚔️', '🛡️', '🚬', '⚰️', '🪦', '⚱️', '🏺', '🔮', '📿', '🧿', '💈', '⚗️', '🔭', '🔬', '🕳️', '🩹', '🩺', '💊', '💉', '🩸', '🧬', '🦠', '🧫', '🧪', '🌡️', '🧹', '🪠', '🧺', '🧻', '🚽', '🚰', '🚿', '🛁', '🛀', '🧼', '🪥', '🪒', '🧽', '🪣', '🧴', '🛎️', '🔑', '🗝️', '🚪', '🪑', '🛋️', '🛏️', '🛌', '🧸', '🪆', '🖼️', '🪞', '🪟', '🛍️', '🛒', '🎁', '🎈', '🎏', '🎀', '🪄', '🪅', '🎊', '🎉', '🎎', '🏮', '🎐', '🧧', '✉️', '📩', '📨', '📧', '💌', '📥', '📤', '📦', '🏷️', '🪧', '📪', '📫', '📬', '📭', '📮', '📯', '📜', '📃', '📄', '📑', '🧾', '📊', '📈', '📉', '🗒️', '🗓️', '📆', '📅', '🗑️', '📇', '🗃️', '🗳️', '🗄️', '📋', '📁', '📂', '🗂️', '🗞️', '📰', '📓', '📔', '📒', '📕', '📗', '📘', '📙', '📚', '📖', '🔖', '🧷', '🔗', '📎', '🖇️', '📐', '📏', '🧮', '📌', '📍', '✂️', '🖊️', '🖋️', '✒️', '🖌️', '🖍️', '📝', '✏️', '🔍', '🔎', '🔏', '🔐', '🔒', '🔓']
  },
  {
    name: 'Nature',
    emojis: ['🌱', '🌿', '🍀', '🍃', '🍂', '🍁', '🌾', '🌲', '🌳', '🌴', '🌵', '🌶️', '🍄', '🌰', '🌻', '🌺', '🌸', '🌼', '🌷', '🥀', '🌹', '🏵️', '💮', '🌋', '⛰️', '🏔️', '🗻', '🏕️', '🏖️', '🏜️', '🏝️', '🏞️', '🌅', '🌄', '🌠', '🎇', '🎆', '🌇', '🌆', '🏙️', '🌃', '🌌', '🌉', '🌁', '⭐', '🌟', '💫', '✨', '☄️', '🌙', '🌛', '🌜', '🌚', '🌕', '🌖', '🌗', '🌘', '🌑', '🌒', '🌓', '🌔', '🌍', '🌎', '🌏', '🪐', '💧', '🌊', '☔', '⛈️', '🌩️', '🌨️', '❄️', '☃️', '⛄', '🌬️', '💨', '🌪️', '🌫️', '🌈', '☀️', '🌤️', '⛅', '🌥️', '☁️', '🌦️', '⛈️', '🌧️', '⚡', '🔥', '💥', '❄️', '🌨️', '☃️', '⛄']
  },
  {
    name: 'Food & Drink',
    emojis: ['🍎', '🍏', '🍐', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🫐', '🍈', '🍒', '🍑', '🥭', '🍍', '🥥', '🥝', '🍅', '🍆', '🥑', '🥦', '🥬', '🥒', '🌶️', '🫑', '🌽', '🥕', '🫒', '🧄', '🧅', '🥔', '🍠', '🥐', '🥯', '🍞', '🥖', '🥨', '🧀', '🥚', '🍳', '🧈', '🥞', '🧇', '🥓', '🥩', '🍗', '🍖', '🦴', '🌭', '🍔', '🍟', '🍕', '🫓', '🥪', '🥙', '🧆', '🌮', '🌯', '🫔', '🥗', '🥘', '🫕', '🥫', '🍝', '🍜', '🍲', '🍛', '🍣', '🍱', '🥟', '🦪', '🍤', '🍙', '🍚', '🍘', '🍥', '🥠', '🥮', '🍢', '🍡', '🍧', '🍨', '🍦', '🥧', '🧁', '🍰', '🎂', '🍮', '🍭', '🍬', '🍫', '🍿', '🍩', '🍪', '🌰', '🥜', '🍯', '🥛', '🍼', '☕', '🫖', '🍵', '🧃', '🥤', '🧋', '🍶', '🍺', '🍻', '🥂', '🍷', '🥃', '🍸', '🍹', '🧉', '🍾']
  }
];

// Element tabs
const ELEMENT_TABS = [
  { id: 'basic', label: 'Basic', icon: <CategoryIcon /> },
  { id: 'shapes', label: 'Shapes', icon: <ShapesIcon /> },
  { id: 'emojis', label: 'Emojis', icon: <EmojiIcon /> }
];

// Gradient presets
const GRADIENT_PRESETS = [
  {
    name: 'Ocean Blue',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    colors: ['#667eea', '#764ba2']
  },
  {
    name: 'Sunset',
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    colors: ['#f093fb', '#f5576c']
  },
  {
    name: 'Forest',
    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    colors: ['#4facfe', '#00f2fe']
  },
  {
    name: 'Purple Rain',
    gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    colors: ['#a8edea', '#fed6e3']
  },
  {
    name: 'Fire',
    gradient: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    colors: ['#ff9a9e', '#fecfef']
  },
  {
    name: 'Cool Sky',
    gradient: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)',
    colors: ['#a1c4fd', '#c2e9fb']
  },
  {
    name: 'Warm Flame',
    gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    colors: ['#ffecd2', '#fcb69f']
  },
  {
    name: 'Night Sky',
    gradient: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
    colors: ['#2c3e50', '#3498db']
  }
];

// Brand color accents
const BRAND_ACCENTS = [
  {
    name: 'LinkedIn Blue',
    primary: '#0077b5',
    secondary: '#004182',
    accent: '#00a0dc',
    light: '#e6f3ff'
  },
  {
    name: 'Tech Green',
    primary: '#10b981',
    secondary: '#047857',
    accent: '#34d399',
    light: '#ecfdf5'
  },
  {
    name: 'Creative Purple',
    primary: '#8b5cf6',
    secondary: '#5b21b6',
    accent: '#a78bfa',
    light: '#f3f4f6'
  },
  {
    name: 'Professional Navy',
    primary: '#1e40af',
    secondary: '#1e3a8a',
    accent: '#3b82f6',
    light: '#eff6ff'
  },
  {
    name: 'Modern Orange',
    primary: '#f97316',
    secondary: '#ea580c',
    accent: '#fb923c',
    light: '#fff7ed'
  },
  {
    name: 'Elegant Pink',
    primary: '#ec4899',
    secondary: '#be185d',
    accent: '#f472b6',
    light: '#fdf2f8'
  }
];

// Preset colors
const presetColors = [
  '#000000', '#FFFFFF', '#3B82F6', '#EF4444', '#10B981', '#F59E0B',
  '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
  '#1F2937', '#6B7280', '#9CA3AF', '#D1D5DB', '#FEE2E2', '#DBEAFE'
];

// Font families - Poppins moved to front as default
const fontFamilies = [
  'Poppins', 'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat',
  'Arial', 'Arial Black', 'Helvetica', 'Times New Roman', 'Georgia', 
  'Verdana', 'Trebuchet MS', 'Impact', 'Comic Sans MS', 'Courier New'
];

const FabricBannerDesigner = () => {
  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const { templateId } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [selectedObject, setSelectedObject] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyStep, setHistoryStep] = useState(-1);
  const [activeSection, setActiveSection] = useState('elements');
  const [expandedPanels, setExpandedPanels] = useState({});
  const [zoom, setZoom] = useState(0.9);
  const [showGrid, setShowGrid] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [selectedColor, setSelectedColor] = useState('#3B82F6');
  const [canvasObjects, setCanvasObjects] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [documentName, setDocumentName] = useState('LinkedIn Banner');
  const [background, setBackground] = useState('#ffffff');
  const [fontSizeInput, setFontSizeInput] = useState('');
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [activeElementTab, setActiveElementTab] = useState(0);
  const [showPexels, setShowPexels] = useState(false);
  const [activeBackgroundTab, setActiveBackgroundTab] = useState(0);
  const [selectedBrandAccent, setSelectedBrandAccent] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isTemplateLoaded, setIsTemplateLoaded] = useState(false);
  const [showProfilePicture, setShowProfilePicture] = useState(true);
  const profilePictureRef = useRef(null);
  const [isRemovingBackground, setIsRemovingBackground] = useState(false);

  // Function to load template elements into the canvas
  const loadTemplateElement = useCallback((canvas, element) => {
    let fabricObject = null;

    switch (element.type) {
      case 'rectangle':
        fabricObject = new fabric.Rect({
          left: element.x,
          top: element.y,
          width: element.width,
          height: element.height,
          fill: element.fill,
          draggable: element.draggable !== false,
          id: element.id
        });
        break;

      case 'circle':
        fabricObject = new fabric.Circle({
          left: element.x - (element.radius || 50),
          top: element.y - (element.radius || 50),
          radius: element.radius || 50,
          fill: element.fill,
          opacity: element.opacity || 1,
          draggable: element.draggable !== false,
          id: element.id
        });
        break;

      case 'line':
        fabricObject = new fabric.Line(element.points || [element.x, element.y, element.x + 100, element.y], {
          stroke: element.stroke || '#000000',
          strokeWidth: element.strokeWidth || 2,
          draggable: element.draggable !== false,
          id: element.id
        });
        break;

      case 'heading':
      case 'paragraph':
        // Calculate appropriate width for text to prevent wrapping issues
        const textLength = element.text ? element.text.length : 10;
        const estimatedWidth = element.fontSize ? textLength * (element.fontSize * 0.6) : textLength * 12;
        const defaultWidth = Math.max(estimatedWidth, element.width || 400);
        
        fabricObject = new fabric.Textbox(element.text, {
          left: element.x,
          top: element.y,
          width: element.type === 'heading' ? Math.max(defaultWidth, 600) : (element.width || defaultWidth),
          fontSize: element.fontSize || 20,
          fontFamily: element.fontFamily || 'Poppins',
          fontWeight: element.fontWeight || 'normal',
          fill: element.fill || '#000000',
          textAlign: element.align || 'left',
          originX: element.align === 'center' ? 'center' : 'left',
          originY: 'top',
          splitByGrapheme: true,
          editable: true,
          id: element.id
        });
        break;

      case 'image-placeholder':
        // Create image placeholder
        fabricObject = new fabric.Rect({
          left: element.x,
          top: element.y,
          width: element.width || 100,
          height: element.height || 100,
          fill: '#F3F4F6',
          stroke: '#D1D5DB',
          strokeWidth: 2,
          strokeDashArray: [5, 5],
          rx: element.cornerRadius || 0,
          ry: element.cornerRadius || 0,
          id: element.id,
          placeholderType: 'image',
          placeholderLabel: element.label || 'Image'
        });
        
        // Add placeholder text
        const placeholderText = new fabric.Text(element.label || '🖼️ Image', {
          left: element.x + (element.width || 100) / 2,
          top: element.y + (element.height || 100) / 2,
          fontSize: Math.min(16, (element.width || 100) / 8),
          fontFamily: 'Inter',
          fontWeight: '500',
          fill: '#9CA3AF',
          textAlign: 'center',
          originX: 'center',
          originY: 'center',
          selectable: false,
          evented: false,
          id: element.id + '_text'
        });
        
        canvas.add(fabricObject);
        canvas.add(placeholderText);
        return; // Don't add fabricObject again below

      case 'logo-placeholder':
        // Create logo placeholder
        fabricObject = new fabric.Rect({
          left: element.x,
          top: element.y,
          width: element.width || 120,
          height: element.height || 40,
          fill: '#F9FAFB',
          stroke: '#E5E7EB',
          strokeWidth: 1,
          strokeDashArray: [3, 3],
          rx: 4,
          ry: 4,
          id: element.id,
          placeholderType: 'logo',
          placeholderLabel: element.label || 'Logo'
        });
        
        const logoText = new fabric.Text(element.label || 'LOGO', {
          left: element.x + (element.width || 120) / 2,
          top: element.y + (element.height || 40) / 2,
          fontSize: Math.min(12, (element.height || 40) / 3),
          fontFamily: 'Inter',
          fontWeight: '600',
          fill: '#6B7280',
          textAlign: 'center',
          originX: 'center',
          originY: 'center',
          selectable: false,
          evented: false,
          id: element.id + '_text'
        });
        
        canvas.add(fabricObject);
        canvas.add(logoText);
        return;

      default:
        console.warn('Unknown element type:', element.type);
        return;
    }

    if (fabricObject) {
      canvas.add(fabricObject);
    }
  }, []);

  // Navigation handler
  const handleBackToTemplates = () => {
    navigate('/banner-templates');
  };

  // Profile picture feature removed - functionality disabled

  // Profile picture toggle removed

  // Replace placeholder with image
  const replacePlaceholder = useCallback((placeholderObject, imageUrl) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !placeholderObject) return;

    fabric.Image.fromURL(imageUrl, (img) => {
      // Get placeholder dimensions and position
      const { left, top, width, height, scaleX = 1, scaleY = 1 } = placeholderObject;
      
      // Scale image to fit placeholder
      const targetWidth = width * scaleX;
      const targetHeight = height * scaleY;
      const imgScale = Math.min(targetWidth / img.width, targetHeight / img.height);
      
      img.set({
        left: left,
        top: top,
        scaleX: imgScale,
        scaleY: imgScale,
        originX: 'left',
        originY: 'top',
        id: placeholderObject.id
      });

      // Remove placeholder and its text
      const placeholderText = canvas.getObjects().find(obj => obj.id === placeholderObject.id + '_text');
      if (placeholderText) canvas.remove(placeholderText);
      canvas.remove(placeholderObject);
      
      // Add new image
      canvas.add(img);
      canvas.setActiveObject(img);
      canvas.renderAll();
      
      setSnackbar({ open: true, message: 'Image replaced successfully!', severity: 'success' });
    }, { crossOrigin: 'anonymous' });
  }, []);

  // Handle file upload for placeholder replacement
  const handleImageUpload = useCallback((file, placeholderObject) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        replacePlaceholder(placeholderObject, e.target.result);
      };
      reader.readAsDataURL(file);
    }
  }, [replacePlaceholder]);

  // Remove background from image using client-side processing
  const removeBackground = useCallback(async (imageObject) => {
    if (!imageObject || imageObject.type !== 'image') {
      setSnackbar({ open: true, message: 'Please select an image first.', severity: 'warning' });
      return;
    }

    setIsRemovingBackground(true);
    
    try {
      // Get the image element
      const canvas = fabricCanvasRef.current;
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      
      // Set canvas size to match image
      tempCanvas.width = imageObject.width * imageObject.scaleX;
      tempCanvas.height = imageObject.height * imageObject.scaleY;
      
      // Draw the image on temporary canvas
      tempCtx.drawImage(
        imageObject.getElement(),
        0, 0,
        tempCanvas.width,
        tempCanvas.height
      );
      
      // Convert to blob for API processing
      tempCanvas.toBlob(async (blob) => {
        try {
          // Option 1: Using remove.bg API (requires API key)
          const removeWithAPI = async (imageBlob) => {
            const formData = new FormData();
            formData.append('image_file', imageBlob);
            formData.append('size', 'auto');
            
            const response = await fetch('https://api.remove.bg/v1.0/removebg', {
              method: 'POST',
              headers: {
                'X-Api-Key': process.env.REACT_APP_REMOVE_BG_API_KEY
              },
              body: formData
            });
            
            if (!response.ok) throw new Error('API request failed');
            return await response.blob();
          };

          // Option 2: Simple edge detection fallback (basic background removal)
          const removeWithCanvas = async (imageBlob) => {
            return new Promise((resolve) => {
              const img = new Image();
              img.onload = () => {
                const bgCanvas = document.createElement('canvas');
                const bgCtx = bgCanvas.getContext('2d');
                bgCanvas.width = img.width;
                bgCanvas.height = img.height;
                
                bgCtx.drawImage(img, 0, 0);
                const imageData = bgCtx.getImageData(0, 0, bgCanvas.width, bgCanvas.height);
                const data = imageData.data;
                
                // Simple background removal: make white/light pixels transparent
                for (let i = 0; i < data.length; i += 4) {
                  const r = data[i];
                  const g = data[i + 1];
                  const b = data[i + 2];
                  
                  // If pixel is close to white/light gray, make it transparent
                  const brightness = (r + g + b) / 3;
                  if (brightness > 200 && Math.abs(r - g) < 30 && Math.abs(g - b) < 30) {
                    data[i + 3] = 0; // Make transparent
                  }
                }
                
                bgCtx.putImageData(imageData, 0, 0);
                bgCanvas.toBlob(resolve, 'image/png');
              };
              img.src = URL.createObjectURL(imageBlob);
            });
          };

          // Try API first, fallback to canvas processing
          let processedBlob;
          try {
            // For demo purposes, we'll use the canvas method
            // In production, you'd implement the API call with user's API key
            processedBlob = await removeWithCanvas(blob);
          } catch (apiError) {
            console.log('API failed, using fallback method');
            processedBlob = await removeWithCanvas(blob);
          }
          
          // Create new image URL
          const processedUrl = URL.createObjectURL(processedBlob);
          
          // Replace the original image with processed one
          fabric.Image.fromURL(processedUrl, (newImg) => {
            // Copy properties from original image
            newImg.set({
              left: imageObject.left,
              top: imageObject.top,
              scaleX: imageObject.scaleX,
              scaleY: imageObject.scaleY,
              angle: imageObject.angle,
              id: imageObject.id
            });
            
            // Remove old image and add new one
            canvas.remove(imageObject);
            canvas.add(newImg);
            canvas.setActiveObject(newImg);
            canvas.renderAll();
            
            setSnackbar({ 
              open: true, 
              message: 'Background removed successfully!', 
              severity: 'success' 
            });
          }, { crossOrigin: 'anonymous' });
          
        } catch (error) {
          console.error('Background removal failed:', error);
          setSnackbar({ 
            open: true, 
            message: 'Background removal failed. Please try again.', 
            severity: 'error' 
          });
        }
      }, 'image/png');
      
    } catch (error) {
      console.error('Background removal error:', error);
      setSnackbar({ 
        open: true, 
        message: 'Background removal failed. Please try again.', 
        severity: 'error' 
      });
    } finally {
      setIsRemovingBackground(false);
    }
  }, []);

  // AI-powered background removal
  const removeBackgroundAI = useCallback(async (imageObject) => {
    if (!imageObject || imageObject.type !== 'image') {
      setSnackbar({ 
        open: true, 
        message: 'Please select an image to remove background.', 
        severity: 'warning' 
      });
      return;
    }

    setIsRemovingBackground(true);
    
    try {
      setSnackbar({ 
        open: true, 
        message: 'Loading AI model and processing image...', 
        severity: 'info' 
      });

      // Get image element from Fabric.js object
      const imageElement = imageObject.getElement();
      
      // Check if AI model is available and get info
      const modelInfo = aiBackgroundRemover.getModelInfo();
      console.log('AI Model Info:', modelInfo);
      
      // Try AI background removal for people first
      let processedBlob;
      let usedMethod = 'unknown';
      
      try {
        processedBlob = await aiBackgroundRemover.removeBackground(imageElement, {
          edgeBlur: 2
        });
        
        // Get the actual method used
        const finalModelInfo = aiBackgroundRemover.getModelInfo();
        usedMethod = finalModelInfo.modelType;
        
        if (usedMethod === 'MediaPipe') {
        setSnackbar({ 
          open: true, 
            message: 'AI background removal completed successfully! (MediaPipe)', 
          severity: 'success' 
        });
        } else if (usedMethod === 'BodyPix') {
          setSnackbar({ 
            open: true, 
            message: 'AI background removal completed successfully! (BodyPix)', 
            severity: 'success' 
          });
        } else if (usedMethod === 'basic') {
          setSnackbar({ 
            open: true, 
            message: 'Background removed using enhanced color detection.', 
            severity: 'success' 
          });
        }
        
      } catch (error) {
        console.log('Person detection failed, trying general object removal:', error.message);
        
        // Fallback to general object removal
        try {
          processedBlob = await aiBackgroundRemover.removeBackgroundGeneral(imageElement);
          usedMethod = 'general';
          
          setSnackbar({ 
            open: true, 
            message: 'Background removal completed! (General object detection)', 
            severity: 'success' 
          });
          
        } catch (generalError) {
          console.log('General AI removal failed, using enhanced simple method');
          
          // Final fallback to enhanced simple method
          processedBlob = await enhancedSimpleRemoval(imageElement);
          usedMethod = 'enhanced_simple';
          
          setSnackbar({ 
            open: true, 
            message: 'Background removed using enhanced algorithm.', 
            severity: 'success' 
          });
        }
      }
      
      // Create new image URL
      const processedUrl = URL.createObjectURL(processedBlob);
      
      // Replace the original image with processed one
      fabric.Image.fromURL(processedUrl, (newImg) => {
        // Copy properties from original image
        newImg.set({
          left: imageObject.left,
          top: imageObject.top,
          scaleX: imageObject.scaleX,
          scaleY: imageObject.scaleY,
          angle: imageObject.angle,
          id: imageObject.id
        });
        
        // Remove old image and add new one
        fabricCanvasRef.current.remove(imageObject);
        fabricCanvasRef.current.add(newImg);
        fabricCanvasRef.current.setActiveObject(newImg);
        fabricCanvasRef.current.renderAll();
        
        // Add to history for undo functionality
        setTimeout(() => {
          const canvas = fabricCanvasRef.current;
          if (canvas) {
            const state = JSON.stringify(canvas.toJSON());
            setHistory(prev => {
              const newHistory = prev.slice(0, historyStep + 1);
              newHistory.push(state);
              setHistoryStep(newHistory.length - 1);
              return newHistory;
            });
          }
        }, 100);
        
        console.log(`Background removal completed using method: ${usedMethod}`);
      }, { crossOrigin: 'anonymous' });
      
    } catch (error) {
      console.error('AI background removal failed:', error);
      setSnackbar({ 
        open: true, 
        message: `Background removal failed: ${error.message}. Please try the simple method.`, 
        severity: 'error' 
      });
    } finally {
      setIsRemovingBackground(false);
    }
  }, [historyStep]);

  // Enhanced simple background removal method
  const enhancedSimpleRemoval = useCallback(async (imageElement) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      canvas.width = imageElement.width || imageElement.naturalWidth;
      canvas.height = imageElement.height || imageElement.naturalHeight;
      
      ctx.drawImage(imageElement, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      // Multi-algorithm approach with improved sampling
      const edgePixels = [];
      const step = Math.max(1, Math.floor(Math.min(canvas.width, canvas.height) / 50));
      
      // Sample edge pixels more comprehensively
      for (let x = 0; x < canvas.width; x += step) {
        // Top edge
        const topIndex = x * 4;
        edgePixels.push([data[topIndex], data[topIndex + 1], data[topIndex + 2]]);
        
        // Bottom edge
        const bottomIndex = ((canvas.height - 1) * canvas.width + x) * 4;
        edgePixels.push([data[bottomIndex], data[bottomIndex + 1], data[bottomIndex + 2]]);
      }
      
      for (let y = 0; y < canvas.height; y += step) {
        // Left edge
        const leftIndex = (y * canvas.width) * 4;
        edgePixels.push([data[leftIndex], data[leftIndex + 1], data[leftIndex + 2]]);
        
        // Right edge
        const rightIndex = (y * canvas.width + canvas.width - 1) * 4;
        edgePixels.push([data[rightIndex], data[rightIndex + 1], data[rightIndex + 2]]);
      }
      
      // Calculate dominant background color
      const avgBg = [0, 0, 0];
      edgePixels.forEach(([r, g, b]) => {
        avgBg[0] += r; 
        avgBg[1] += g; 
        avgBg[2] += b;
      });
      avgBg[0] = Math.round(avgBg[0] / edgePixels.length);
      avgBg[1] = Math.round(avgBg[1] / edgePixels.length);
      avgBg[2] = Math.round(avgBg[2] / edgePixels.length);
      
      // Process pixels with improved algorithm
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i], g = data[i + 1], b = data[i + 2];
        
        // Calculate color distance
        const distance = Math.sqrt(
          Math.pow(r - avgBg[0], 2) +
          Math.pow(g - avgBg[1], 2) +
          Math.pow(b - avgBg[2], 2)
        );
        
        // Check if pixel is similar to white/light colors (common backgrounds)
        const brightness = (r + g + b) / 3;
        const isWhiteish = brightness > 200 && 
          Math.abs(r - g) < 40 && Math.abs(g - b) < 40 && Math.abs(r - b) < 40;
        
        // Adaptive threshold based on background characteristics
        const baseThreshold = 70;
        const threshold = isWhiteish ? baseThreshold * 0.8 : baseThreshold;
        const fadeThreshold = threshold * 1.6;
        
        if (distance < threshold || isWhiteish) {
          data[i + 3] = 0; // Make transparent
        } else if (distance < fadeThreshold) {
          // Smooth edges with better alpha blending
          const alpha = (distance - threshold) / (fadeThreshold - threshold);
          data[i + 3] = Math.round(data[i + 3] * Math.min(alpha, 1));
        }
      }
      
      ctx.putImageData(imageData, 0, 0);
      canvas.toBlob(resolve, 'image/png');
    });
  }, []);



  // Load template from URL parameter
  useEffect(() => {
    if (templateId) {
      const template = bannerTemplates.find(t => t.id === templateId);
      if (template) {
        setSelectedTemplate(template);
        setDocumentName(template.name);
        if (template.template && template.template.canvasSettings) {
          setBackground(template.template.canvasSettings.background || '#ffffff');
          setZoom(template.template.canvasSettings.zoom || 0.9);
        }
        setSnackbar({ 
          open: true, 
          message: `Template "${template.name}" loaded successfully!`, 
          severity: 'success' 
        });
      } else {
        setSnackbar({ 
          open: true, 
          message: 'Template not found. Starting with blank canvas.', 
          severity: 'warning' 
        });
      }
    }
    setIsTemplateLoaded(true);
  }, [templateId]);

  // Initialize Fabric.js canvas
  useEffect(() => {
    if (!isTemplateLoaded) return;

    const canvas = new fabric.Canvas(canvasRef.current, {
      width: CANVAS_WIDTH,
      height: CANVAS_HEIGHT,
      backgroundColor: background,
      selection: true,
      preserveObjectStacking: true
    });

    fabricCanvasRef.current = canvas;

    // Enhanced event handlers
    canvas.on('mouse:dblclick', function(e) {
              if (e.target && (e.target.type === 'textbox' || e.target.type === 'i-text')) {
        e.target.enterEditing();
        e.target.selectAll();
      }
    });

    canvas.on('selection:created', function(e) {
      const obj = e.selected[0];
      setSelectedObject(obj);
      if (obj && obj.type === 'textbox') {
        setFontSizeInput(obj.fontSize || 20);
      }
      updateObjectsList();
      // Auto-close left panel when selecting an element
      setActiveSection(null);
    });

    canvas.on('selection:updated', function(e) {
      const obj = e.selected[0];
      setSelectedObject(obj);
      if (obj && obj.type === 'textbox') {
        setFontSizeInput(obj.fontSize || 20);
      }
      updateObjectsList();
      // Auto-close left panel when selecting an element
      setActiveSection(null);
    });

    canvas.on('selection:cleared', function() {
      setSelectedObject(null);
      setFontSizeInput('');
    });

    canvas.on('object:modified object:added object:removed', function() {
      saveState();
      updateObjectsList();
    });

        // Load template elements if template is selected
    if (selectedTemplate && selectedTemplate.template && selectedTemplate.template.pages && selectedTemplate.template.pages[0]) {
      const templatePage = selectedTemplate.template.pages[0];
      templatePage.elements.forEach(element => {
        loadTemplateElement(canvas, element);
      });
      canvas.renderAll();
    }

    // Profile picture overlay removed

    // Save initial state
    setTimeout(() => {
    saveState();
      updateObjectsList();
    }, 100);

    return () => {
      canvas.dispose();
    };
  }, [isTemplateLoaded, background, selectedTemplate]);

  // Profile picture overlay functionality removed

  // Update objects list for layers panel
  const updateObjectsList = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (canvas) {
      const objects = canvas.getObjects().map((obj, index) => ({
        id: obj.id || index,
        type: obj.type,
        name: obj.type === 'textbox' ? obj.text?.slice(0, 20) + '...' : obj.type,
        visible: obj.visible !== false,
        object: obj
      }));
      setCanvasObjects(objects);
    }
  }, []);

  // Save canvas state for undo/redo
  const saveState = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (canvas) {
      const state = JSON.stringify(canvas.toJSON());
      const newHistory = history.slice(0, historyStep + 1);
      newHistory.push(state);
      setHistory(newHistory);
      setHistoryStep(newHistory.length - 1);
    }
  }, [history, historyStep]);

  // Undo/Redo functions
  const undo = useCallback(() => {
    if (historyStep > 0) {
      const canvas = fabricCanvasRef.current;
      const previousState = history[historyStep - 1];
      canvas.loadFromJSON(previousState, () => {
        canvas.renderAll();
        setHistoryStep(historyStep - 1);
        setSelectedObject(null);
        updateObjectsList();
      });
    }
  }, [history, historyStep, updateObjectsList]);

  const redo = useCallback(() => {
    if (historyStep < history.length - 1) {
      const canvas = fabricCanvasRef.current;
      const nextState = history[historyStep + 1];
      canvas.loadFromJSON(nextState, () => {
        canvas.renderAll();
        setHistoryStep(historyStep + 1);
        setSelectedObject(null);
        updateObjectsList();
      });
    }
  }, [history, historyStep, updateObjectsList]);

  // Element creation functions

  const addHeading = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    const text = new fabric.Textbox('Your Professional Title', {
      left: CANVAS_WIDTH / 2 - 200,
      top: CANVAS_HEIGHT / 2 - 30,
      width: 400,
      fontSize: 48,
      fontFamily: 'Poppins',
      fontWeight: 'bold',
      fill: selectedColor,
      textAlign: 'left',
      splitByGrapheme: true,
      id: 'heading_' + Date.now()
    });

    canvas.add(text);
    canvas.setActiveObject(text);
    canvas.renderAll();
    setSelectedObject(text);
    setFontSizeInput(text.fontSize);
    setSnackbar({ open: true, message: 'Heading added successfully!', severity: 'success' });
  }, [selectedColor]);

  const addParagraph = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    const text = new fabric.Textbox('Add your professional description or value proposition here. Make it compelling and engaging.', {
      left: CANVAS_WIDTH / 2 - 200,
      top: CANVAS_HEIGHT / 2 - 15,
      width: 400,
      fontSize: 18,
      fontFamily: 'Poppins',
      fontWeight: 'normal',
      fill: selectedColor,
      textAlign: 'left',
      splitByGrapheme: true,
      id: 'paragraph_' + Date.now()
    });

    canvas.add(text);
    canvas.setActiveObject(text);
    canvas.renderAll();
    setSelectedObject(text);
    setFontSizeInput(text.fontSize);
    setSnackbar({ open: true, message: 'Paragraph added successfully!', severity: 'success' });
  }, [selectedColor]);

  const addShape = useCallback((shapeType) => {
    const canvas = fabricCanvasRef.current;
    let shape;

    switch (shapeType) {
      case 'rectangle':
        shape = new fabric.Rect({
          left: CANVAS_WIDTH / 2 - 60,
          top: CANVAS_HEIGHT / 2 - 40,
          width: 120,
          height: 80,
          fill: selectedColor,
          stroke: '#000000',
          strokeWidth: 2,
          id: 'rect_' + Date.now()
        });
        break;
      case 'circle':
        shape = new fabric.Circle({
          left: CANVAS_WIDTH / 2 - 50,
          top: CANVAS_HEIGHT / 2 - 50,
          radius: 50,
          fill: selectedColor,
          stroke: '#000000',
          strokeWidth: 2,
          id: 'circle_' + Date.now()
        });
        break;
      case 'line':
        shape = new fabric.Line([0, 0, 100, 0], {
          left: CANVAS_WIDTH / 2 - 50,
          top: CANVAS_HEIGHT / 2,
          stroke: selectedColor,
          strokeWidth: 3,
          fill: '',
          id: 'line_' + Date.now()
        });
        break;
      case 'arrow':
        // Create arrow using path
        const arrowPath = 'M 0 10 L 80 10 L 70 0 L 100 15 L 70 30 L 80 20 L 0 20 Z';
        shape = new fabric.Path(arrowPath, {
          left: CANVAS_WIDTH / 2 - 50,
          top: CANVAS_HEIGHT / 2 - 15,
          fill: selectedColor,
          stroke: '#000000',
          strokeWidth: 1,
          scaleX: 1,
          scaleY: 1,
          id: 'arrow_' + Date.now()
        });
        break;
      case 'star':
        // Create a star path
        const starPath = 'M 50 0 L 61.8 35 L 100 35 L 69.1 57.5 L 80.9 92.5 L 50 70 L 19.1 92.5 L 30.9 57.5 L 0 35 L 38.2 35 Z';
        shape = new fabric.Path(starPath, {
          left: CANVAS_WIDTH / 2 - 50,
          top: CANVAS_HEIGHT / 2 - 50,
          fill: selectedColor,
          stroke: '#000000',
          strokeWidth: 1,
          scaleX: 0.8,
          scaleY: 0.8,
          id: 'star_' + Date.now()
        });
        break;
      case 'polygon':
        // Create hexagon
        const hexPoints = [];
        const hexRadius = 50;
        for (let i = 0; i < 6; i++) {
          const angle = (i * Math.PI) / 3;
          hexPoints.push({
            x: Math.cos(angle) * hexRadius,
            y: Math.sin(angle) * hexRadius
          });
        }
        shape = new fabric.Polygon(hexPoints, {
          left: CANVAS_WIDTH / 2,
          top: CANVAS_HEIGHT / 2,
          fill: selectedColor,
          stroke: '#000000',
          strokeWidth: 2,
          originX: 'center',
          originY: 'center',
          id: 'polygon_' + Date.now()
        });
        break;
      case 'ellipse':
        shape = new fabric.Ellipse({
          left: CANVAS_WIDTH / 2 - 60,
          top: CANVAS_HEIGHT / 2 - 40,
          rx: 60,
          ry: 40,
          fill: selectedColor,
          stroke: '#000000',
          strokeWidth: 2,
          id: 'ellipse_' + Date.now()
        });
        break;
      case 'wedge':
        // Create wedge using path (pie slice)
        const wedgePath = 'M 50 50 L 50 0 A 50 50 0 0 1 85.36 64.64 Z';
        shape = new fabric.Path(wedgePath, {
          left: CANVAS_WIDTH / 2 - 50,
          top: CANVAS_HEIGHT / 2 - 50,
          fill: selectedColor,
          stroke: '#000000',
          strokeWidth: 2,
          id: 'wedge_' + Date.now()
        });
        break;
      case 'arc':
        // Create arc using path
        const arcPath = 'M 10 50 A 40 40 0 0 1 90 50';
        shape = new fabric.Path(arcPath, {
          left: CANVAS_WIDTH / 2 - 50,
          top: CANVAS_HEIGHT / 2 - 25,
          fill: '',
          stroke: selectedColor,
          strokeWidth: 4,
          id: 'arc_' + Date.now()
        });
        break;
      case 'ring':
        // Create ring using two circles
        const outerCircle = new fabric.Circle({
          radius: 50,
          fill: selectedColor,
          originX: 'center',
          originY: 'center'
        });
        const innerCircle = new fabric.Circle({
          radius: 30,
          fill: 'white',
          originX: 'center',
          originY: 'center'
        });
        shape = new fabric.Group([outerCircle, innerCircle], {
          left: CANVAS_WIDTH / 2,
          top: CANVAS_HEIGHT / 2,
          originX: 'center',
          originY: 'center',
          id: 'ring_' + Date.now()
        });
        break;
      default:
        return;
    }

    canvas.add(shape);
    canvas.setActiveObject(shape);
    canvas.renderAll();
    setSelectedObject(shape);
    setSnackbar({ open: true, message: `${shapeType} added successfully!`, severity: 'success' });
  }, [selectedColor]);

  const addImage = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          fabric.Image.fromURL(event.target.result, (img) => {
            const canvas = fabricCanvasRef.current;
            
            // Smart scaling
            const maxWidth = 400;
            const maxHeight = 300;
            const scaleX = maxWidth / img.width;
            const scaleY = maxHeight / img.height;
            const scale = Math.min(scaleX, scaleY, 1);
            
            img.set({
              left: CANVAS_WIDTH / 2 - (img.width * scale) / 2,
              top: CANVAS_HEIGHT / 2 - (img.height * scale) / 2,
              scaleX: scale,
              scaleY: scale,
              id: 'image_' + Date.now()
            });

            canvas.add(img);
            canvas.setActiveObject(img);
            canvas.renderAll();
            setSnackbar({ open: true, message: 'Image added successfully!', severity: 'success' });
          });
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  }, []);

  // Apply template
  const applyTemplate = useCallback((template) => {
    const canvas = fabricCanvasRef.current;
    canvas.clear();
    
    // Set background
    canvas.setBackgroundColor(template.elements[0].color, canvas.renderAll.bind(canvas));
    setBackground(template.elements[0].color);
    
    // Add template elements
    template.elements.slice(1).forEach((element, index) => {
      if (element.type === 'text') {
        const text = new fabric.Textbox(element.text, {
          left: element.x,
          top: element.y,
          fontSize: element.fontSize,
          fill: element.color,
          fontWeight: element.fontWeight || 'normal',
          fontFamily: 'Inter',
          width: 600,
          id: `template_text_${index}`
        });
        canvas.add(text);
      }
    });
    
    canvas.renderAll();
    setSnackbar({ open: true, message: `Template "${template.name}" applied!`, severity: 'success' });
  }, []);

  // Delete selected object
  const deleteSelected = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      canvas.remove(activeObject);
      canvas.renderAll();
      setSelectedObject(null);
      setSnackbar({ open: true, message: 'Object deleted!', severity: 'info' });
    }
  }, []);

  // Duplicate selected object
  const duplicateSelected = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      activeObject.clone((cloned) => {
        cloned.set({
          left: cloned.left + 20,
          top: cloned.top + 20,
          id: activeObject.type + '_' + Date.now()
        });
        canvas.add(cloned);
        canvas.setActiveObject(cloned);
        canvas.renderAll();
        setSnackbar({ open: true, message: 'Object duplicated!', severity: 'success' });
      });
    }
  }, []);

  // Update object property
  const updateObjectProperty = useCallback((property, value) => {
    if (selectedObject && fabricCanvasRef.current) {
      selectedObject.set(property, value);
      
      // Special handling for text objects
      if (selectedObject.type === 'textbox') {
        // For textbox objects, we need to trigger a re-render of the text
        selectedObject.initDimensions();
        selectedObject.setCoords();
      }
      
      fabricCanvasRef.current.renderAll();
      
      // Force component re-render to update the properties panel
      setSelectedObject(fabricCanvasRef.current.getActiveObject());
      updateObjectsList();
      
      // Save state for undo/redo
      saveState();
    }
  }, [selectedObject, updateObjectsList, saveState]);

  // Download banner
  const downloadBanner = useCallback((format = 'png') => {
    const canvas = fabricCanvasRef.current;
    
    // Temporarily hide profile picture overlay for export
    const overlayElements = canvas.getObjects().filter(obj => 
      obj.id === 'profile-picture-overlay' || obj.id === 'profile-picture-text'
    );
    
    overlayElements.forEach(element => {
      element.visible = false;
    });
    
    canvas.renderAll();
    
    const dataURL = canvas.toDataURL({
      format: format,
      quality: 1,
      multiplier: 2
    });
    
    // Restore overlay visibility
    overlayElements.forEach(element => {
      element.visible = true;
    });
    
    canvas.renderAll();
    
    const link = document.createElement('a');
    link.download = `${documentName}.${format}`;
    link.href = dataURL;
    link.click();
    
    setSnackbar({ open: true, message: 'Banner downloaded successfully!', severity: 'success' });
  }, [documentName]);

  // Add emoji
  const addEmoji = useCallback((emoji) => {
    const canvas = fabricCanvasRef.current;
    
    const text = new fabric.Text(emoji, {
      left: CANVAS_WIDTH / 2,
      top: CANVAS_HEIGHT / 2,
      fontSize: 64,
      fontFamily: 'Arial',
      originX: 'center',
      originY: 'center',
      id: 'emoji_' + Date.now()
    });

    canvas.add(text);
    canvas.setActiveObject(text);
    canvas.renderAll();
    setSnackbar({ open: true, message: 'Emoji added successfully!', severity: 'success' });
  }, []);

  // Handle Pexels image selection
  const handlePexelsImage = useCallback((image) => {
    const canvas = fabricCanvasRef.current;

    fabric.Image.fromURL(image.src.large, (img) => {
      // Check if we're replacing a placeholder
      const activeObject = canvas.getActiveObject();
      let targetLeft, targetTop, targetScaleX, targetScaleY;
      
      if (activeObject && (activeObject.placeholderType === 'image' || activeObject.placeholderType === 'logo')) {
        // Replace placeholder
        targetLeft = activeObject.left;
        targetTop = activeObject.top;
        const targetWidth = activeObject.width * activeObject.scaleX;
        const targetHeight = activeObject.height * activeObject.scaleY;
        const scaleX = targetWidth / img.width;
        const scaleY = targetHeight / img.height;
        const scale = Math.min(scaleX, scaleY);
        targetScaleX = targetScaleY = scale;
        
        // Remove placeholder and its text
        const placeholderText = canvas.getObjects().find(obj => obj.id === activeObject.id + '_text');
        if (placeholderText) canvas.remove(placeholderText);
        canvas.remove(activeObject);
      } else {
        // Add as new image
      const maxWidth = CANVAS_WIDTH * 0.8;
      const maxHeight = CANVAS_HEIGHT * 0.8;
      const scaleX = maxWidth / img.width;
      const scaleY = maxHeight / img.height;
      const scale = Math.min(scaleX, scaleY);
        
        targetLeft = CANVAS_WIDTH / 2;
        targetTop = CANVAS_HEIGHT / 2;
        targetScaleX = targetScaleY = scale;
      }
      
      img.set({
        left: targetLeft,
        top: targetTop,
        originX: activeObject && activeObject.placeholderType ? 'left' : 'center',
        originY: activeObject && activeObject.placeholderType ? 'top' : 'center',
        scaleX: targetScaleX,
        scaleY: targetScaleY,
        id: 'pexels_' + Date.now()
      });

      canvas.add(img);
      canvas.setActiveObject(img);
      canvas.renderAll();
      setSnackbar({ open: true, message: 'Stock image added successfully!', severity: 'success' });
    }, { crossOrigin: 'anonymous' });
  }, []);

  // Apply gradient background
  const applyGradient = useCallback((gradientPreset) => {
    const canvas = fabricCanvasRef.current;
    
    // Create gradient
    const gradient = new fabric.Gradient({
      type: 'linear',
      coords: {
        x1: 0,
        y1: 0,
        x2: CANVAS_WIDTH,
        y2: CANVAS_HEIGHT
      },
      colorStops: [
        { offset: 0, color: gradientPreset.colors[0] },
        { offset: 1, color: gradientPreset.colors[1] }
      ]
    });

    canvas.setBackgroundColor(gradient, canvas.renderAll.bind(canvas));
    setBackground(gradientPreset.gradient);
    setSnackbar({ open: true, message: `${gradientPreset.name} gradient applied!`, severity: 'success' });
  }, []);

  // Apply brand accent colors
  const applyBrandAccent = useCallback((brandAccent) => {
    setSelectedBrandAccent(brandAccent);
    setSelectedColor(brandAccent.primary);
    setSnackbar({ open: true, message: `${brandAccent.name} brand colors selected!`, severity: 'success' });
  }, []);

  // Handle section clicks
  const handleSectionClick = (section) => {
    setActiveSection(activeSection === section ? null : section);
  };

  const togglePanel = (panel) => {
    setExpandedPanels(prev => ({
      ...prev,
      [panel]: !prev[panel]
    }));
  };



  // Zoom functions
  const handleZoomChange = (event, newValue) => {
    setZoom(newValue);
  };

  // Sidebar sections
  const sidebarSections = [
    { id: 'elements', label: 'Elements', icon: <ElementsIcon /> },
    { id: 'text', label: 'Text', icon: <TextIcon /> },
    { id: 'photos', label: 'Photos', icon: <PhotosIcon /> },
    { id: 'background', label: 'Background', icon: <BackgroundIcon /> },
    { id: 'branding', label: 'Branding', icon: <Palette /> },
    { id: 'layers', label: 'Layers', icon: <LayersIcon /> }
  ];

  // Keyboard shortcuts - moved after all function definitions
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Prevent shortcuts when typing in input fields
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const ctrlKey = isMac ? e.metaKey : e.ctrlKey;

      switch (e.key.toLowerCase()) {
        case 'z':
          if (ctrlKey && !e.shiftKey) {
            e.preventDefault();
            undo();
          }
          break;
        case 'y':
          if (ctrlKey) {
            e.preventDefault();
            redo();
          }
          break;
        case 'z':
          if (ctrlKey && e.shiftKey) {
            e.preventDefault();
            redo();
          }
          break;
        case 'd':
          if (ctrlKey) {
            e.preventDefault();
            duplicateSelected();
          }
          break;
        case 's':
          if (ctrlKey) {
            e.preventDefault();
            downloadBanner('png');
          }
          break;
        case 'delete':
        case 'backspace':
          if (!ctrlKey && !e.shiftKey) {
            e.preventDefault();
            deleteSelected();
          }
          break;
        case 'escape':
          if (fabricCanvasRef.current) {
            fabricCanvasRef.current.discardActiveObject();
            fabricCanvasRef.current.renderAll();
            setSelectedObject(null);
          }
          break;

        case 'r':
          if (!ctrlKey) {
            e.preventDefault();
            addShape('rectangle');
          }
          break;
        case 'c':
          if (!ctrlKey) {
            e.preventDefault();
            addShape('circle');
          }
          break;
        case 'a':
          if (ctrlKey && fabricCanvasRef.current) {
            e.preventDefault();
            const objects = fabricCanvasRef.current.getObjects();
            if (objects.length > 0) {
              fabricCanvasRef.current.setActiveObject(objects[0]);
              for (let i = 1; i < objects.length; i++) {
                fabricCanvasRef.current.setActiveObject(objects[i]);
              }
              fabricCanvasRef.current.renderAll();
            }
          }
          break;
        case '?':
        case '/':
          if (!ctrlKey) {
            e.preventDefault();
            setShowShortcuts(true);
          }
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, duplicateSelected, deleteSelected, downloadBanner, addShape]);

  // Left Panel Content based on active section
  const renderLeftPanelContent = () => {
    switch (activeSection) {
      case 'elements':
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Elements</Typography>
            <Tabs 
              value={activeElementTab} 
              onChange={(e, newValue) => setActiveElementTab(newValue)}
              variant="fullWidth"
              sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              {ELEMENT_TABS.map((tab) => (
                <Tab 
                  key={tab.id}
                  label={tab.label}
                  icon={tab.icon}
                  iconPosition="start"
                  sx={{ textTransform: 'none', fontSize: '0.75rem', minHeight: 48 }}
                />
              ))}
            </Tabs>
            
            {/* Basic Elements Tab */}
            {activeElementTab === 0 && (
              <Stack spacing={2}>

                <ModernButton startIcon={<HeadingIcon />} onClick={addHeading} fullWidth>
                  Heading
                </ModernButton>
                <ModernButton startIcon={<ParagraphIcon />} onClick={addParagraph} fullWidth>
                  Paragraph
                </ModernButton>
                <ModernButton startIcon={<ImageIcon />} onClick={addImage} fullWidth>
                  Upload Image
                </ModernButton>
              </Stack>
            )}
            
            {/* Shapes Tab */}
            {activeElementTab === 1 && (
              <Grid container spacing={1}>
                {SHAPES.map((shape) => (
                  <Grid item xs={4} key={shape.id}>
                    <ElementButton onClick={() => addShape(shape.id)}>
                      {shape.icon}
                      <Typography variant="caption">{shape.name}</Typography>
                    </ElementButton>
                  </Grid>
                ))}
              </Grid>
            )}
            
            {/* Emojis Tab */}
            {activeElementTab === 2 && (
              <Box>
                {EMOJI_CATEGORIES.map((category) => (
                  <Box key={category.name} sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#64748b' }}>
                      {category.name}
                    </Typography>
                    <Grid container spacing={0.5}>
                      {category.emojis.slice(0, 24).map((emoji) => (
                        <Grid item key={emoji}>
                          <Button
                            onClick={() => addEmoji(emoji)}
                            sx={{
                              minWidth: 32,
                              width: 32,
                              height: 32,
                              p: 0,
                              fontSize: '1.2rem',
                              '&:hover': { bgcolor: 'rgba(72, 144, 254, 0.08)' }
                            }}
                          >
                            {emoji}
                          </Button>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        );

      case 'text':
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Text</Typography>
            <Stack spacing={1}>
              <ModernButton startIcon={<HeadingIcon />} onClick={addHeading} fullWidth>
                Add Heading
              </ModernButton>
              <ModernButton startIcon={<ParagraphIcon />} onClick={addParagraph} fullWidth>
                Add Paragraph
              </ModernButton>
            </Stack>
          </Box>
        );

      case 'photos':
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Photos</Typography>
            <Stack spacing={2}>
              <ModernButton startIcon={<UploadIcon />} onClick={addImage} fullWidth>
                Upload Image
              </ModernButton>
              <ModernButton 
                startIcon={<PhotosIcon />} 
                onClick={() => setShowPexels(true)} 
                fullWidth
                sx={{ 
                  color: '#4890fe',
                  border: '1px solid #4890fe',
                  '&:hover': { 
                    bgcolor: 'rgba(72, 144, 254, 0.08)',
                    borderColor: '#4890fe'
                  }
                }}
              >
                Stock Images
              </ModernButton>
              
              <Divider sx={{ my: 1 }} />
              
              {/* Background Removal Tool */}
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#64748b', mb: 1 }}>
                Image Tools
              </Typography>
              
              <ModernButton 
                startIcon={<AutoFixHigh />} 
                onClick={() => {
                  const activeObject = fabricCanvasRef.current?.getActiveObject();
                  if (activeObject && activeObject.type === 'image') {
                    removeBackground(activeObject);
                  } else {
                    setSnackbar({ 
                      open: true, 
                      message: 'Please select an image to remove its background.', 
                      severity: 'warning' 
                    });
                  }
                }}
                disabled={isRemovingBackground}
                fullWidth
                sx={{ 
                  color: '#10B981',
                  border: '1px solid #10B981',
                  '&:hover': { 
                    bgcolor: 'rgba(16, 185, 129, 0.08)',
                    borderColor: '#059669'
                  },
                  '&:disabled': {
                    border: '1px solid #D1D5DB',
                    color: '#9CA3AF'
                  }
                }}
              >
                {isRemovingBackground ? 'Processing...' : 'Remove Background'}
              </ModernButton>
              
              <ModernButton 
                startIcon={<StarIcon />} 
                onClick={() => {
                  const activeObject = fabricCanvasRef.current?.getActiveObject();
                  if (activeObject && activeObject.type === 'image') {
                    removeBackgroundAI(activeObject);
                  } else {
                    setSnackbar({ 
                      open: true, 
                      message: 'Please select an image to use AI background removal.', 
                      severity: 'warning' 
                    });
                  }
                }}
                disabled={isRemovingBackground}
                fullWidth
                sx={{ 
                  color: '#8B5CF6',
                  border: '1px solid #8B5CF6',
                  '&:hover': { 
                    bgcolor: 'rgba(139, 92, 246, 0.08)',
                    borderColor: '#7C3AED'
                  },
                  '&:disabled': {
                    border: '1px solid #D1D5DB',
                    color: '#9CA3AF'
                  }
                }}
              >
                AI Background Removal
              </ModernButton>
            </Stack>
          </Box>
        );

      case 'background':
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Background</Typography>
            <Tabs 
              value={activeBackgroundTab} 
              onChange={(e, newValue) => setActiveBackgroundTab(newValue)}
              variant="fullWidth"
              sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Colors" sx={{ textTransform: 'none', fontSize: '0.75rem', minHeight: 48 }} />
              <Tab label="Gradients" sx={{ textTransform: 'none', fontSize: '0.75rem', minHeight: 48 }} />
              <Tab label="Custom" sx={{ textTransform: 'none', fontSize: '0.75rem', minHeight: 48 }} />
            </Tabs>
            
            {/* Solid Colors Tab */}
            {activeBackgroundTab === 0 && (
              <Box>
                <Typography variant="body2" sx={{ mb: 2, color: '#64748b' }}>
                  Preset Colors
                </Typography>
                <Grid container spacing={1} sx={{ mb: 3 }}>
                  {presetColors.slice(0, 18).map((color) => (
                    <Grid item xs={2} key={color}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: color,
                          border: background === color ? '3px solid #4890fe' : '2px solid #e2e8f0',
                          borderRadius: 1,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': { transform: 'scale(1.1)' }
                        }}
                        onClick={() => {
                          setBackground(color);
                          fabricCanvasRef.current.setBackgroundColor(color, fabricCanvasRef.current.renderAll.bind(fabricCanvasRef.current));
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
            
            {/* Gradients Tab */}
            {activeBackgroundTab === 1 && (
              <Box>
                <Typography variant="body2" sx={{ mb: 2, color: '#64748b' }}>
                  Gradient Presets
                </Typography>
                <Grid container spacing={2}>
                  {GRADIENT_PRESETS.map((gradient) => (
                    <Grid item xs={6} key={gradient.name}>
                      <Box
                        sx={{
                          height: 60,
                          background: gradient.gradient,
                          borderRadius: 2,
                          cursor: 'pointer',
                          border: '2px solid transparent',
                          transition: 'all 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          '&:hover': { 
                            transform: 'scale(1.02)',
                            border: '2px solid #4890fe'
                          }
                        }}
                        onClick={() => applyGradient(gradient)}
                      >
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: 'white',
                            fontWeight: 'bold',
                            textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                            textAlign: 'center'
                          }}
                        >
                          {gradient.name}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
            
            {/* Custom Tab */}
            {activeBackgroundTab === 2 && (
              <Box>
                <Typography variant="body2" sx={{ mb: 2, color: '#64748b' }}>
                  Custom Color Picker
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <HexColorPicker
                    color={background}
                    onChange={(color) => {
                      setBackground(color);
                      fabricCanvasRef.current.setBackgroundColor(color, fabricCanvasRef.current.renderAll.bind(fabricCanvasRef.current));
                    }}
                    style={{ width: '200px', height: '200px' }}
                  />
                </Box>
                <TextField
                  label="Hex Color"
                  value={background}
                  onChange={(e) => {
                    setBackground(e.target.value);
                    fabricCanvasRef.current.setBackgroundColor(e.target.value, fabricCanvasRef.current.renderAll.bind(fabricCanvasRef.current));
                  }}
                  fullWidth
                  size="small"
                />
              </Box>
            )}
          </Box>
        );

      case 'branding':
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Branding</Typography>
            
            {/* Active Brand Colors - Moved to Top */}
            {selectedBrandAccent && (
              <Box sx={{ mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: '#1e293b' }}>
                  Active Brand Colors - {selectedBrandAccent.name}
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          backgroundColor: selectedBrandAccent.primary,
                          borderRadius: 1,
                          mx: 'auto',
                          mb: 1,
                          cursor: 'pointer',
                          border: selectedColor === selectedBrandAccent.primary ? '3px solid #4890fe' : '2px solid white',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          transition: 'all 0.2s',
                          '&:hover': { transform: 'scale(1.1)' }
                        }}
                        onClick={() => setSelectedColor(selectedBrandAccent.primary)}
                      />
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>Primary</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          backgroundColor: selectedBrandAccent.secondary,
                          borderRadius: 1,
                          mx: 'auto',
                          mb: 1,
                          cursor: 'pointer',
                          border: selectedColor === selectedBrandAccent.secondary ? '3px solid #4890fe' : '2px solid white',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          transition: 'all 0.2s',
                          '&:hover': { transform: 'scale(1.1)' }
                        }}
                        onClick={() => setSelectedColor(selectedBrandAccent.secondary)}
                      />
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>Secondary</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          backgroundColor: selectedBrandAccent.accent,
                          borderRadius: 1,
                          mx: 'auto',
                          mb: 1,
                          cursor: 'pointer',
                          border: selectedColor === selectedBrandAccent.accent ? '3px solid #4890fe' : '2px solid white',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          transition: 'all 0.2s',
                          '&:hover': { transform: 'scale(1.1)' }
                        }}
                        onClick={() => setSelectedColor(selectedBrandAccent.accent)}
                      />
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>Accent</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          backgroundColor: selectedBrandAccent.light,
                          borderRadius: 1,
                          mx: 'auto',
                          mb: 1,
                          cursor: 'pointer',
                          border: selectedColor === selectedBrandAccent.light ? '3px solid #4890fe' : '1px solid #e2e8f0',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          transition: 'all 0.2s',
                          '&:hover': { transform: 'scale(1.1)' }
                        }}
                        onClick={() => setSelectedColor(selectedBrandAccent.light)}
                      />
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>Light</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
            
            <Typography variant="body2" sx={{ mb: 2, color: '#64748b' }}>
              Professional Color Palettes
            </Typography>
            
            <Stack spacing={2}>
              {BRAND_ACCENTS.map((brand) => (
                <Card 
                  key={brand.name}
                  sx={{ 
                    cursor: 'pointer',
                    border: selectedBrandAccent?.name === brand.name ? '2px solid #4890fe' : '1px solid #e2e8f0',
                    transition: 'all 0.2s',
                    '&:hover': { 
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }
                  }}
                  onClick={() => applyBrandAccent(brand)}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {brand.name}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          backgroundColor: brand.primary,
                          borderRadius: '50%',
                          border: '2px solid white',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      />
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          backgroundColor: brand.secondary,
                          borderRadius: '50%',
                          border: '2px solid white',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      />
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          backgroundColor: brand.accent,
                          borderRadius: '50%',
                          border: '2px solid white',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      />
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          backgroundColor: brand.light,
                          borderRadius: '50%',
                          border: '1px solid #e2e8f0'
                        }}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Box>
        );

      case 'layers':
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Layers ({canvasObjects.length})
            </Typography>
            <List dense>
              {[...canvasObjects].reverse().map((obj, index) => (
                <ListItem key={obj.id || index} disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    selected={selectedObject === obj.object}
                    onClick={() => {
                      fabricCanvasRef.current.setActiveObject(obj.object);
                      fabricCanvasRef.current.renderAll();
                      setSelectedObject(obj.object);
                    }}
                    sx={{ 
                      borderRadius: 1, 
                      border: '1px solid #e2e8f0',
                      '&:hover': { bgcolor: 'rgba(72, 144, 254, 0.04)' }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {obj.type === 'textbox' && <TextIcon fontSize="small" />}
                      {obj.type === 'rect' && <RectangleIcon fontSize="small" />}
                      {obj.type === 'circle' && <CircleIcon fontSize="small" />}
                      {obj.type === 'image' && <ImageIcon fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText 
                      primary={obj.name}
                      primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 'medium' }}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        obj.object.set('visible', !obj.visible);
                        fabricCanvasRef.current.renderAll();
                        updateObjectsList();
                      }}
                    >
                      {obj.visible ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                    </IconButton>
                  </ListItemButton>
                </ListItem>
              ))}
              {canvasObjects.length === 0 && (
                <Typography variant="body2" sx={{ color: '#64748b', textAlign: 'center', py: 4 }}>
                  No elements added yet
                </Typography>
              )}
            </List>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ 
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      bgcolor: 'background.default',
      overflow: 'auto',
    }}>
      <Box sx={{ 
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#f8fafc',
        overflow: 'hidden'
      }}>
        {/* Enhanced Upper Toolbar */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          height: 80,
          px: 3,
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          bgcolor: 'white',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
        }}>
          {/* Logo Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 4 }}>
            <a href="/" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center' }}>
              <img src={InstationLogo} alt="Instation" style={{ height: 42, width: 'auto' }} />
            </a>
            <Divider orientation="vertical" flexItem sx={{ mx: 2, height: 40 }} />
            <Typography variant="h6" sx={{ 
              fontWeight: 700, 
              color: '#1e293b',
              letterSpacing: '-0.5px'
            }}>
              Banner Designer
            </Typography>
            <Button
              onClick={handleBackToTemplates}
              startIcon={<ArrowBackIcon />}
              variant="outlined"
              size="small"
              sx={{ 
                ml: 2,
                textTransform: 'none',
                borderColor: '#e2e8f0',
                color: '#64748b',
                '&:hover': {
                  borderColor: '#cbd5e1',
                  bgcolor: '#f8fafc'
                }
              }}
            >
              Back to Templates
            </Button>
          </Box>
          
          {/* Document Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 4 }}>
            <TextField
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              variant="outlined"
              size="small"
              placeholder="Banner name..."
              sx={{ 
                minWidth: 220,
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#f8fafc',
                  '&:hover': { bgcolor: '#f1f5f9' },
                  '&.Mui-focused': { bgcolor: 'white' }
                }
              }}
            />
            <Chip 
              label={`${CANVAS_WIDTH}×${CANVAS_HEIGHT}`} 
              size="small" 
              sx={{ 
                ml: 2, 
                bgcolor: '#e0f2fe', 
                color: '#0369a1',
                fontWeight: 600,
                fontSize: '0.75rem'
              }} 
            />
          </Box>
          

          
          {/* Edit Actions */}
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mr: 3 }}>
            <Tooltip title="Undo (Ctrl+Z)" arrow>
              <span>
                <IconButton 
                  onClick={undo} 
                  disabled={historyStep <= 0} 
                  size="small"
                  sx={{
                    bgcolor: historyStep > 0 ? 'rgba(156, 163, 175, 0.1)' : 'transparent',
                    '&:hover': { bgcolor: 'rgba(156, 163, 175, 0.2)' },
                    '&:disabled': { bgcolor: 'transparent' },
                    borderRadius: 2
                  }}
                >
                  <UndoIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Redo (Ctrl+Y)" arrow>
              <span>
                <IconButton 
                  onClick={redo} 
                  disabled={historyStep >= history.length - 1} 
                  size="small"
                  sx={{
                    bgcolor: historyStep < history.length - 1 ? 'rgba(156, 163, 175, 0.1)' : 'transparent',
                    '&:hover': { bgcolor: 'rgba(156, 163, 175, 0.2)' },
                    '&:disabled': { bgcolor: 'transparent' },
                    borderRadius: 2
                  }}
                >
                  <RedoIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
            
            <Divider orientation="vertical" flexItem sx={{ mx: 1, height: 24 }} />
            
            <Tooltip title="Duplicate (Ctrl+D)" arrow>
              <span>
                <IconButton 
                  onClick={duplicateSelected} 
                  disabled={!selectedObject} 
                  size="small"
                  sx={{
                    bgcolor: selectedObject ? 'rgba(16, 185, 129, 0.1)' : 'transparent',
                    '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.2)' },
                    '&:disabled': { bgcolor: 'transparent' },
                    borderRadius: 2
                  }}
                >
                  <ContentCopy sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete (Del)" arrow>
              <span>
                <IconButton 
                  onClick={deleteSelected} 
                  disabled={!selectedObject} 
                  size="small"
                  sx={{
                    bgcolor: selectedObject ? 'rgba(239, 68, 68, 0.1)' : 'transparent',
                    '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.2)' },
                    '&:disabled': { bgcolor: 'transparent' },
                    borderRadius: 2
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          
          {/* View Controls */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mr: 3 }}>
            <FormControlLabel
              control={
                <Switch 
                  checked={showGrid} 
                  onChange={(e) => setShowGrid(e.target.checked)}
                  size="small"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#4890fe',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#4890fe',
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#64748b' }}>
                  Grid
                </Typography>
              }
            />
            

            
            <Box sx={{ width: 120 }}>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#64748b', fontSize: '0.75rem' }}>
                Zoom: {Math.round(zoom * 100)}%
              </Typography>
              <Slider
                value={zoom}
                onChange={handleZoomChange}
                min={0.1}
                max={2}
                step={0.1}
                size="small"
                sx={{
                  color: '#4890fe',
                  '& .MuiSlider-thumb': {
                    width: 14,
                    height: 14,
                  },
                  '& .MuiSlider-track': {
                    height: 2,
                  },
                  '& .MuiSlider-rail': {
                    height: 2,
                  },
                }}
              />
            </Box>
          </Stack>
          
          <Box sx={{ flexGrow: 1 }} />
          
          {/* Export Actions */}
          <Stack direction="row" spacing={1}>
            <Tooltip title="Keyboard Shortcuts (?)" arrow>
              <IconButton
                onClick={() => setShowShortcuts(true)}
                size="small"
                sx={{ 
                  borderColor: '#d1d5db',
                  color: '#64748b',
                  border: '1px solid #d1d5db',
                  '&:hover': { 
                    borderColor: '#9ca3af',
                    bgcolor: 'rgba(0, 0, 0, 0.02)'
                  },
                  borderRadius: 2,
                  mr: 1
                }}
              >
                <KeyboardIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => downloadBanner('png')}
              sx={{ 
                bgcolor: '#059669',
                fontWeight: 600,
                px: 3,
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(5, 150, 105, 0.25)',
                '&:hover': {
                  bgcolor: '#047857',
                  boxShadow: '0 6px 16px rgba(5, 150, 105, 0.35)',
                  transform: 'translateY(-1px)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              Export PNG
            </Button>
          </Stack>
        </Box>
        
        <Box sx={{ 
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          bgcolor: '#f8fafc',
        }}>
          {/* Left Sidebar - Fixed Position */}
          <SidebarContainer sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            zIndex: 1000,
            height: '100%'
          }}>
            {sidebarSections.map((section) => (
              <Tooltip 
                key={section.id}
                title={section.label}
                placement="right"
                arrow
              >
                <SidebarItem
                  active={activeSection === section.id}
                  onClick={() => handleSectionClick(section.id)}
                  sx={{
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateX(2px)',
                    }
                  }}
                >
                  <SidebarIcon active={activeSection === section.id}>
                    {React.cloneElement(section.icon, { 
                      sx: { fontSize: 20 } 
                    })}
                  </SidebarIcon>
                  <SidebarLabel active={activeSection === section.id}>
                    {section.label}
                  </SidebarLabel>
                </SidebarItem>
              </Tooltip>
            ))}
          </SidebarContainer>

          {/* Canvas Area - Full Width */}
          <Box sx={{ 
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: '#f1f5f9',
            backgroundImage: showGrid ? 'radial-gradient(circle, #e2e8f0 1px, transparent 1px)' : 'none',
            backgroundSize: showGrid ? '20px 20px' : 'auto',
            p: 2,
            overflow: 'auto',
            position: 'relative'
          }}>
            <Paper
              sx={{
                boxShadow: '0 10px 25px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                overflow: 'hidden',
                transform: `scale(${zoom})`
              }}
            >
              <canvas 
                ref={canvasRef} 
                style={{ 
                  display: 'block',
                  maxWidth: '100%',
                  height: 'auto'
                }} 
              />
            </Paper>
          </Box>

          {/* Left Panel Content - Overlay */}
          {activeSection && (
            <Paper sx={{ 
              position: 'absolute',
              left: 80,
              top: 0,
              width: 320, 
              height: '100%', 
              borderRadius: 0, 
              borderRight: '1px solid rgba(0, 0, 0, 0.08)',
              overflow: 'auto',
              zIndex: 999,
              boxShadow: '4px 0 12px rgba(0, 0, 0, 0.15)',
              bgcolor: 'white'
            }}>
              {renderLeftPanelContent()}
            </Paper>
          )}

          {/* Right Panel - Properties - Overlay */}
          {selectedObject && (
            <Paper sx={{ 
              position: 'absolute',
              right: 0,
              top: 0,
              width: 320, 
              height: '100%', 
              borderRadius: 0, 
              borderLeft: '1px solid rgba(0, 0, 0, 0.08)',
              overflow: 'auto',
              p: 0,
              bgcolor: '#fafbfc',
              zIndex: 999,
              boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.15)'
            }}>
              <Box sx={{ 
                p: 2, 
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                bgcolor: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <Typography variant="h6" sx={{ 
                  mb: 0, 
                  fontWeight: 'bold',
                  color: '#1e293b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <Settings sx={{ fontSize: 20, color: '#4890fe' }} />
                  Properties
                </Typography>
                <Tooltip title="Close Properties" arrow>
                  <IconButton 
                    size="small" 
                    onClick={() => {
                      fabricCanvasRef.current.discardActiveObject();
                      fabricCanvasRef.current.renderAll();
                      setSelectedObject(null);
                    }}
                    sx={{
                      color: '#64748b',
                      '&:hover': { 
                        bgcolor: 'rgba(100, 116, 139, 0.08)',
                        color: '#374151'
                      }
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              
              <Box sx={{ p: 2 }}>
              
              {/* Placeholder Controls */}
              {selectedObject && (selectedObject.placeholderType === 'image' || selectedObject.placeholderType === 'logo') && (
                <Stack spacing={3}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                    {selectedObject.placeholderType === 'image' ? 'Image' : 'Logo'} Placeholder
                  </Typography>
                  
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Replace with your {selectedObject.placeholderType}
                    </Typography>
                    
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      id={`placeholder-upload-${selectedObject.id}`}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          handleImageUpload(file, selectedObject);
                        }
                      }}
                    />
                    
                    <Stack spacing={2}>
                      <Button
                        variant="contained"
                        startIcon={<UploadIcon />}
                        onClick={() => {
                          document.getElementById(`placeholder-upload-${selectedObject.id}`).click();
                        }}
                        fullWidth
                        sx={{
                          bgcolor: '#4890fe',
                          py: 1.5,
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          '&:hover': { bgcolor: '#3b7dd8' }
                        }}
                      >
                        Upload {selectedObject.placeholderType === 'image' ? 'Image' : 'Logo'}
                      </Button>
                      
                      <Button
                        variant="outlined"
                        startIcon={<PhotosIcon />}
                        onClick={() => setShowPexels(true)}
                        fullWidth
                        sx={{
                          py: 1.5,
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500,
                          borderColor: '#d1d5db',
                          color: '#374151',
                          '&:hover': { 
                            borderColor: '#9ca3af',
                            bgcolor: 'rgba(0, 0, 0, 0.02)'
                          }
                        }}
                      >
                        Choose from Stock Photos
                      </Button>
                    </Stack>
                  </Box>
                </Stack>
              )}
              
              {selectedObject && selectedObject.type === 'image' && (
                <Stack spacing={3}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                    Image Properties
                  </Typography>
                  
                  {/* Background Removal */}
                  <Box>
                    <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
                      Background Removal
                    </Typography>
                    
                    <Stack spacing={2}>
                      <Button
                        variant="contained"
                        startIcon={isRemovingBackground ? <AutoFixHigh /> : <AutoFixHigh />}
                        onClick={() => removeBackground(selectedObject)}
                        disabled={isRemovingBackground}
                        fullWidth
                        sx={{
                          bgcolor: '#10B981',
                          py: 1.5,
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          '&:hover': { bgcolor: '#059669' },
                          '&:disabled': { bgcolor: '#9CA3AF' }
                        }}
                      >
                        {isRemovingBackground ? 'Removing Background...' : 'Remove Background'}
                      </Button>
                      
                      <Button
                        variant="outlined"
                        startIcon={<StarIcon />}
                        onClick={() => removeBackgroundAI(selectedObject)}
                        disabled={isRemovingBackground}
                        fullWidth
                        sx={{
                          py: 1.5,
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500,
                          borderColor: '#8B5CF6',
                          color: '#8B5CF6',
                          '&:hover': { 
                            borderColor: '#7C3AED',
                            bgcolor: 'rgba(139, 92, 246, 0.04)'
                          },
                          '&:disabled': { 
                            borderColor: '#D1D5DB',
                            color: '#9CA3AF'
                          }
                        }}
                      >
                        AI Background Removal
                      </Button>
                    </Stack>
                  </Box>

                  <Divider />

                  {/* Image Adjustments */}
                  <Box>
                    <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
                      Image Adjustments
                    </Typography>
                    
                    {/* Opacity */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1, fontSize: '0.875rem' }}>
                        Opacity: {Math.round((selectedObject.opacity || 1) * 100)}%
                      </Typography>
                      <Slider
                        value={(selectedObject.opacity || 1) * 100}
                        onChange={(e, value) => updateObjectProperty('opacity', value / 100)}
                        min={0}
                        max={100}
                        step={1}
                        size="small"
                        sx={{ color: '#4890fe' }}
                      />
                    </Box>

                    {/* Scale */}
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, fontSize: '0.875rem' }}>
                        Scale: {Math.round((selectedObject.scaleX || 1) * 100)}%
                      </Typography>
                      <Slider
                        value={(selectedObject.scaleX || 1) * 100}
                        onChange={(e, value) => {
                          const scale = value / 100;
                          updateObjectProperty('scaleX', scale);
                          updateObjectProperty('scaleY', scale);
                        }}
                        min={10}
                        max={300}
                        step={1}
                        size="small"
                        sx={{ color: '#4890fe' }}
                      />
                    </Box>
                  </Box>
                </Stack>
              )}
              
              {selectedObject.type === 'textbox' && (
                <Stack spacing={3}>
                  <TextField
                    label="Text Content"
                    value={selectedObject.text || ''}
                    onChange={(e) => updateObjectProperty('text', e.target.value)}
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                  />
                  
                  <FormControl fullWidth>
                    <InputLabel>Font Family</InputLabel>
                    <Select
                      value={selectedObject.fontFamily || 'Poppins'}
                      onChange={(e) => updateObjectProperty('fontFamily', e.target.value)}
                    >
                      {fontFamilies.map(font => (
                        <MenuItem key={font} value={font}>{font}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  {/* Font Size with Input and Slider */}
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Font Size
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <TextField
                        type="number"
                        value={fontSizeInput}
                        onChange={(e) => {
                          setFontSizeInput(e.target.value);
                        }}
                        onBlur={(e) => {
                          const newSize = parseInt(e.target.value);
                          if (!isNaN(newSize)) {
                            const clampedSize = Math.max(12, Math.min(120, newSize));
                            updateObjectProperty('fontSize', clampedSize);
                            setFontSizeInput(clampedSize);
                          } else {
                            setFontSizeInput(selectedObject.fontSize || 20);
                          }
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.target.blur();
                          }
                        }}
                        size="small"
                        sx={{ width: 80 }}
                        inputProps={{ min: 12, max: 120, step: 1 }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Slider
                          value={Number(selectedObject.fontSize) || 20}
                          onChange={(e, value) => {
                            const newSize = Number(value);
                            updateObjectProperty('fontSize', newSize);
                            setFontSizeInput(newSize);
                          }}
                          min={12}
                          max={120}
                          step={1}
                          size="small"
                          sx={{
                            color: '#4890fe',
                            '& .MuiSlider-thumb': {
                              width: 16,
                              height: 16,
                            },
                            '& .MuiSlider-track': {
                              height: 3,
                            },
                            '& .MuiSlider-rail': {
                              height: 3,
                            },
                          }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ color: '#64748b', minWidth: 30 }}>
                        {selectedObject.fontSize || 20}px
                      </Typography>
                    </Stack>
                  </Box>

                  {/* Font Weight */}
                  <FormControl fullWidth>
                    <InputLabel>Font Weight</InputLabel>
                    <Select
                      value={selectedObject.fontWeight || 'normal'}
                      onChange={(e) => updateObjectProperty('fontWeight', e.target.value)}
                    >
                      <MenuItem value="100">Thin (100)</MenuItem>
                      <MenuItem value="200">Extra Light (200)</MenuItem>
                      <MenuItem value="300">Light (300)</MenuItem>
                      <MenuItem value="400">Normal (400)</MenuItem>
                      <MenuItem value="500">Medium (500)</MenuItem>
                      <MenuItem value="600">Semi Bold (600)</MenuItem>
                      <MenuItem value="700">Bold (700)</MenuItem>
                      <MenuItem value="800">Extra Bold (800)</MenuItem>
                      <MenuItem value="900">Black (900)</MenuItem>
                    </Select>
                  </FormControl>
                  
                  {/* Text Color */}
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Text Color
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                      <HexColorPicker
                        color={selectedObject.fill || '#000000'}
                        onChange={(color) => updateObjectProperty('fill', color)}
                        style={{ width: '150px', height: '150px' }}
                      />
                    </Box>
                    <TextField
                      label="Hex Color"
                      value={selectedObject.fill || '#000000'}
                      onChange={(e) => updateObjectProperty('fill', e.target.value)}
                      fullWidth
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                  
                  {/* Text Formatting */}
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Text Formatting
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Bold">
                        <IconButton
                          onClick={() => updateObjectProperty('fontWeight', 
                            selectedObject.fontWeight === 'bold' || selectedObject.fontWeight === '700' ? 'normal' : 'bold'
                          )}
                          sx={{
                            bgcolor: (selectedObject.fontWeight === 'bold' || selectedObject.fontWeight === '700') ? 'rgba(72, 144, 254, 0.12)' : 'transparent',
                            color: (selectedObject.fontWeight === 'bold' || selectedObject.fontWeight === '700') ? '#4890fe' : '#64748b',
                            '&:hover': { bgcolor: 'rgba(72, 144, 254, 0.08)' }
                          }}
                        >
                          <FormatBold />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Italic">
                        <IconButton
                          onClick={() => updateObjectProperty('fontStyle', 
                            selectedObject.fontStyle === 'italic' ? 'normal' : 'italic'
                          )}
                          sx={{
                            bgcolor: selectedObject.fontStyle === 'italic' ? 'rgba(72, 144, 254, 0.12)' : 'transparent',
                            color: selectedObject.fontStyle === 'italic' ? '#4890fe' : '#64748b',
                            '&:hover': { bgcolor: 'rgba(72, 144, 254, 0.08)' }
                          }}
                        >
                          <FormatItalic />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Underline">
                        <IconButton
                          onClick={() => updateObjectProperty('underline', !selectedObject.underline)}
                          sx={{
                            bgcolor: selectedObject.underline ? 'rgba(72, 144, 254, 0.12)' : 'transparent',
                            color: selectedObject.underline ? '#4890fe' : '#64748b',
                            '&:hover': { bgcolor: 'rgba(72, 144, 254, 0.08)' }
                          }}
                        >
                          <FormatUnderlined />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>

                  {/* Text Alignment */}
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Text Alignment
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Align Left">
                        <IconButton
                          onClick={() => updateObjectProperty('textAlign', 'left')}
                          sx={{
                            bgcolor: selectedObject.textAlign === 'left' ? 'rgba(72, 144, 254, 0.12)' : 'transparent',
                            color: selectedObject.textAlign === 'left' ? '#4890fe' : '#64748b',
                            '&:hover': { bgcolor: 'rgba(72, 144, 254, 0.08)' }
                          }}
                        >
                          <FormatAlignLeft />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Align Center">
                        <IconButton
                          onClick={() => updateObjectProperty('textAlign', 'center')}
                          sx={{
                            bgcolor: selectedObject.textAlign === 'center' ? 'rgba(72, 144, 254, 0.12)' : 'transparent',
                            color: selectedObject.textAlign === 'center' ? '#4890fe' : '#64748b',
                            '&:hover': { bgcolor: 'rgba(72, 144, 254, 0.08)' }
                          }}
                        >
                          <FormatAlignCenter />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Align Right">
                        <IconButton
                          onClick={() => updateObjectProperty('textAlign', 'right')}
                          sx={{
                            bgcolor: selectedObject.textAlign === 'right' ? 'rgba(72, 144, 254, 0.12)' : 'transparent',
                            color: selectedObject.textAlign === 'right' ? '#4890fe' : '#64748b',
                            '&:hover': { bgcolor: 'rgba(72, 144, 254, 0.08)' }
                          }}
                        >
                          <FormatAlignRight />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                </Stack>
              )}

              {(selectedObject.type === 'rect' || selectedObject.type === 'circle' || selectedObject.type === 'ellipse' || selectedObject.type === 'polygon' || selectedObject.type === 'path' || selectedObject.type === 'line' || selectedObject.type === 'group') && (
                <Stack spacing={2}>
                  {/* Fill Color - Not for lines and arcs */}
                  {selectedObject.type !== 'line' && selectedObject.fill !== '' && (
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Fill Color
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                        <HexColorPicker
                          color={selectedObject.fill || '#000000'}
                          onChange={(color) => updateObjectProperty('fill', color)}
                          style={{ width: '150px', height: '150px' }}
                        />
                      </Box>
                      <TextField
                        label="Hex Color"
                        value={selectedObject.fill || '#000000'}
                        onChange={(e) => updateObjectProperty('fill', e.target.value)}
                        fullWidth
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  )}
                  
                  {/* Stroke Color */}
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Stroke Color
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                      <HexColorPicker
                        color={selectedObject.stroke || '#000000'}
                        onChange={(color) => updateObjectProperty('stroke', color)}
                        style={{ width: '150px', height: '150px' }}
                      />
                    </Box>
                    <TextField
                      label="Hex Color"
                      value={selectedObject.stroke || '#000000'}
                      onChange={(e) => updateObjectProperty('stroke', e.target.value)}
                      fullWidth
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                  
                  {/* Stroke Width */}
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Stroke Width: {selectedObject.strokeWidth || 0}px
                    </Typography>
                    <Slider
                      value={selectedObject.strokeWidth || 0}
                      onChange={(e, value) => updateObjectProperty('strokeWidth', value)}
                      min={0}
                      max={20}
                      size="small"
                      sx={{ color: '#4890fe' }}
                    />
                  </Box>
                  
                  {/* Opacity */}
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Opacity: {Math.round((selectedObject.opacity || 1) * 100)}%
                    </Typography>
                    <Slider
                      value={(selectedObject.opacity || 1) * 100}
                      onChange={(e, value) => updateObjectProperty('opacity', value / 100)}
                      min={0}
                      max={100}
                      size="small"
                      sx={{ color: '#4890fe' }}
                    />
                  </Box>
                  
                  {/* Rotation */}
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Rotation: {Math.round(selectedObject.angle || 0)}°
                    </Typography>
                    <Slider
                      value={selectedObject.angle || 0}
                      onChange={(e, value) => updateObjectProperty('angle', value)}
                      min={-180}
                      max={180}
                      size="small"
                      sx={{ color: '#4890fe' }}
                    />
                  </Box>
                  
                  {/* Scale */}
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Scale: {Math.round((selectedObject.scaleX || 1) * 100)}%
                    </Typography>
                    <Slider
                      value={(selectedObject.scaleX || 1) * 100}
                      onChange={(e, value) => {
                        const scale = value / 100;
                        updateObjectProperty('scaleX', scale);
                        updateObjectProperty('scaleY', scale);
                      }}
                      min={10}
                      max={300}
                      size="small"
                      sx={{ color: '#4890fe' }}
                    />
                  </Box>
                  
                  {/* Shadow Toggle */}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={selectedObject.shadow ? true : false}
                        onChange={(e) => {
                          if (e.target.checked) {
                            updateObjectProperty('shadow', new fabric.Shadow({
                              color: 'rgba(0,0,0,0.3)',
                              blur: 10,
                              offsetX: 5,
                              offsetY: 5
                            }));
                          } else {
                            updateObjectProperty('shadow', null);
                          }
                        }}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#4890fe',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#4890fe',
                          },
                        }}
                      />
                    }
                    label="Drop Shadow"
                    sx={{ fontSize: '0.875rem' }}
                  />
                </Stack>
              )}

              <Divider sx={{ my: 3 }} />
              
              <Button
                variant="outlined"
                startIcon={<DeleteIcon />}
                onClick={deleteSelected}
                fullWidth
                sx={{
                  py: 1.5,
                  borderColor: '#ef4444',
                  color: '#ef4444',
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#dc2626',
                    bgcolor: 'rgba(239, 68, 68, 0.04)',
                    color: '#dc2626',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.15)'
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                Delete Element
              </Button>
              </Box>
            </Paper>
          )}
        </Box>
        
        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Keyboard Shortcuts Dialog */}
        <Dialog
          open={showShortcuts}
          onClose={() => setShowShortcuts(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            pb: 1
          }}>
            <KeyboardIcon sx={{ color: '#4890fe' }} />
            Keyboard Shortcuts
          </DialogTitle>
          <DialogContent>
            <Box sx={{ py: 1 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#1e293b', fontWeight: 600 }}>
                Edit Actions
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Undo</Typography>
                    <Chip label="Ctrl+Z" size="small" variant="outlined" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Redo</Typography>
                    <Chip label="Ctrl+Y" size="small" variant="outlined" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Duplicate</Typography>
                    <Chip label="Ctrl+D" size="small" variant="outlined" />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Delete</Typography>
                    <Chip label="Del" size="small" variant="outlined" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Deselect</Typography>
                    <Chip label="Esc" size="small" variant="outlined" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Select All</Typography>
                    <Chip label="Ctrl+A" size="small" variant="outlined" />
                  </Box>
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ mb: 2, color: '#1e293b', fontWeight: 600 }}>
                Quick Tools
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Add Text</Typography>
                    <Chip label="T" size="small" variant="outlined" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Add Rectangle</Typography>
                    <Chip label="R" size="small" variant="outlined" />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Add Circle</Typography>
                    <Chip label="C" size="small" variant="outlined" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Export PNG</Typography>
                    <Chip label="Ctrl+S" size="small" variant="outlined" />
                  </Box>
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ mb: 2, color: '#1e293b', fontWeight: 600 }}>
                Help
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Show Shortcuts</Typography>
                <Chip label="?" size="small" variant="outlined" />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setShowShortcuts(false)}
              sx={{ textTransform: 'none' }}
            >
              Got it!
            </Button>
          </DialogActions>
        </Dialog>

        {/* Pexels Search Dialog */}
        <PexelsSearch
          open={showPexels}
          onClose={() => setShowPexels(false)}
          onSelectImage={handlePexelsImage}
        />
      </Box>
    </Box>
  );
};

export default FabricBannerDesigner; 